import type { Animation } from '@adflow/types';
import {
  getAnimationEndTime,
  sampleAnimationStyles,
  type ElementAnimationStyle,
} from '@adflow/utils';

import { ANIMATION_RENDER_LIMITS } from './limits';

export type AnimationSceneTiming = {
  /** Last keyframe time. Playback and previews run from 0 to this point. */
  durationSeconds: number;
  timelineSeconds: number;
  fps: number;
  loop: boolean;
};

export type RenderRangeError = { error: string };

const MS = 1000;

function roundSeconds(value: number): number {
  return Math.round(value * MS) / MS;
}

export function resolveSceneTiming(animation: Animation | null | undefined): AnimationSceneTiming {
  if (!animation) {
    return { durationSeconds: 0, timelineSeconds: 0, fps: 0, loop: false };
  }
  return {
    durationSeconds: roundSeconds(getAnimationEndTime(animation)),
    timelineSeconds: animation.timeline.duration,
    fps: animation.timeline.fps,
    loop: animation.timeline.loop,
  };
}

/** Per-element styles at a timestamp, keyed by element id. */
export function evaluateSceneAt(
  animation: Animation | null | undefined,
  timeSeconds: number,
): Record<string, ElementAnimationStyle> {
  if (!animation) {
    return {};
  }
  return Object.fromEntries(sampleAnimationStyles(animation, timeSeconds));
}

export function validateTimestamp(
  timeSeconds: unknown,
  timing: AnimationSceneTiming,
): number | RenderRangeError {
  if (typeof timeSeconds !== 'number' || !Number.isFinite(timeSeconds)) {
    return { error: 'timeSeconds must be a finite number of seconds.' };
  }
  if (timeSeconds < 0 || timeSeconds > timing.durationSeconds) {
    return {
      error: `timeSeconds must be between 0 and ${timing.durationSeconds} seconds.`,
    };
  }
  return roundSeconds(timeSeconds);
}

/** Evenly spaced, first and last frame included. */
export function contactSheetTimestamps(
  timing: AnimationSceneTiming,
  requested?: unknown,
): number[] | RenderRangeError {
  const { defaultContactSheetFrames, maxContactSheetFrames } = ANIMATION_RENDER_LIMITS;
  if (requested !== undefined) {
    if (!Array.isArray(requested) || requested.length === 0) {
      return { error: 'timestamps must be a non-empty array of seconds.' };
    }
    if (requested.length > maxContactSheetFrames) {
      return { error: `A contact sheet holds at most ${maxContactSheetFrames} frames.` };
    }
    const resolved: number[] = [];
    for (const value of requested) {
      const timestamp = validateTimestamp(value, timing);
      if (typeof timestamp !== 'number') {
        return timestamp;
      }
      resolved.push(timestamp);
    }
    return resolved;
  }
  if (timing.durationSeconds <= 0) {
    return [0];
  }
  const count = defaultContactSheetFrames;
  return Array.from({ length: count }, (_, index) =>
    roundSeconds((timing.durationSeconds * index) / (count - 1)),
  );
}

export type VideoRange = {
  startSeconds: number;
  endSeconds: number;
  fps: number;
  /** Frame times. The last frame is held for 1/fps. */
  frameTimes: number[];
  durationSeconds: number;
};

export function resolveVideoRange(
  timing: AnimationSceneTiming,
  input: { startSeconds?: unknown; endSeconds?: unknown; fps?: unknown },
): VideoRange | RenderRangeError {
  const { maxVideoFps, defaultVideoFps, maxVideoSeconds } = ANIMATION_RENDER_LIMITS;
  if (timing.durationSeconds <= 0) {
    return { error: 'The template has no animation to record.' };
  }
  const fps = input.fps ?? defaultVideoFps;
  if (typeof fps !== 'number' || !Number.isInteger(fps) || fps < 1 || fps > maxVideoFps) {
    return { error: `fps must be an integer from 1 to ${maxVideoFps}.` };
  }
  const start = input.startSeconds ?? 0;
  const startSeconds = validateTimestamp(start, timing);
  if (typeof startSeconds !== 'number') {
    return { error: `startSeconds: ${startSeconds.error}` };
  }
  const end = input.endSeconds ?? Math.min(timing.durationSeconds, startSeconds + maxVideoSeconds);
  const endSeconds = validateTimestamp(end, timing);
  if (typeof endSeconds !== 'number') {
    return { error: `endSeconds: ${endSeconds.error}` };
  }
  if (endSeconds <= startSeconds) {
    return { error: 'endSeconds must be after startSeconds.' };
  }
  if (endSeconds - startSeconds > maxVideoSeconds) {
    return { error: `A review video covers at most ${maxVideoSeconds} seconds.` };
  }
  const frameCount = Math.max(1, Math.round((endSeconds - startSeconds) * fps)) + 1;
  const frameTimes = Array.from({ length: frameCount }, (_, index) =>
    roundSeconds(Math.min(endSeconds, startSeconds + index / fps)),
  );
  return {
    startSeconds,
    endSeconds,
    fps,
    frameTimes,
    durationSeconds: roundSeconds(frameCount / fps),
  };
}
